import { useEffect, useState } from "react";
import { bankApi, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScrollText, ShieldAlert } from "lucide-react";
import FdicHoldDialog from "@/components/FdicHoldDialog";

export default function TransactionsPage() {
  const [txs, setTxs] = useState([]);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [holdDialog, setHoldDialog] = useState({ open: false, tx: null, fee: 0 });

  const reload = () => {
    return Promise.all([
      bankApi.transactions().then(setTxs),
      bankApi.accounts().then(setBalances),
    ]);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const openHold = (t) =>
    setHoldDialog({ open: true, tx: t, fee: t.fee_amount });

  return (
    <div className="space-y-10">
      <div>
        <div className="text-institutional-gold uppercase tracking-[0.25em] text-xs font-semibold mb-3">
          Master Ledger
        </div>
        <h1 className="font-serif text-5xl text-federal-navy">Transaction history</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          A complete, immutable record of every wire executed from your
          accounts. Transfers on FDIC hold require an insurance fee to be
          released.
        </p>
      </div>

      <div className="gold-divider" />

      <div className="border border-slate-200 bg-white" data-testid="transactions-table">
        {loading ? (
          <div className="p-16 text-center text-slate-500 italic">
            Retrieving ledger…
          </div>
        ) : txs.length === 0 ? (
          <div className="p-16 text-center">
            <ScrollText
              className="w-8 h-8 text-slate-300 mx-auto mb-4"
              strokeWidth={1.5}
            />
            <div className="text-slate-500 italic">
              No transactions recorded on this ledger.
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-6 py-4 font-medium">Timestamp</th>
                <th className="text-left px-6 py-4 font-medium">Type</th>
                <th className="text-left px-6 py-4 font-medium">From</th>
                <th className="text-left px-6 py-4 font-medium">Recipient</th>
                <th className="text-left px-6 py-4 font-medium">Status</th>
                <th className="text-right px-6 py-4 font-medium">Amount</th>
                <th className="text-right px-6 py-4 font-medium">FDIC Fee</th>
                <th className="text-right px-6 py-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => {
                const onHold = t.status === "on_hold_fdic";
                return (
                  <tr
                    key={t.id}
                    className={`border-b border-slate-100 last:border-0 transition-colors ${
                      onHold ? "bg-[#FCF7EA]" : "hover:bg-slate-50"
                    }`}
                    data-testid={`tx-row-${t.id}`}
                  >
                    <td className="px-6 py-5 text-sm text-slate-600 font-mono">
                      {new Date(t.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-5">
                      <span className="uppercase tracking-widest text-[10px] text-institutional-gold font-semibold">
                        {t.transfer_type}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-600 capitalize">
                      {t.from_account}
                    </td>
                    <td className="px-6 py-5 text-sm text-federal-navy font-medium">
                      <div>{t.recipient_name}</div>
                      <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                        {t.to_account_number}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      {onHold ? (
                        <span
                          className="inline-flex items-center gap-1.5 uppercase tracking-widest text-[10px] font-semibold text-[#800000] border border-[#800000] px-2 py-1"
                          data-testid={`tx-status-hold-${t.id}`}
                        >
                          <ShieldAlert className="w-3 h-3" strokeWidth={1.5} />
                          FDIC Hold
                        </span>
                      ) : (
                        <span className="uppercase tracking-widest text-[10px] font-semibold text-[#1E5631] border border-[#1E5631] px-2 py-1 inline-block">
                          Completed
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-sm text-right tabular-nums font-medium text-[#800000]">
                      − {formatMoney(t.amount)}
                    </td>
                    <td className="px-6 py-5 text-sm text-right tabular-nums text-slate-600 font-mono">
                      {formatMoney(t.fee_amount || 0)}
                    </td>
                    <td className="px-6 py-5 text-right">
                      {onHold ? (
                        <Button
                          size="sm"
                          onClick={() => openHold(t)}
                          data-testid={`pay-fdic-fee-btn-${t.id}`}
                          className="rounded-none bg-federal-navy hover:bg-federal-navy/90 text-white uppercase tracking-widest text-[10px] h-8"
                        >
                          Pay FDIC fee
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">
                          Released
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <FdicHoldDialog
        open={holdDialog.open}
        onOpenChange={(v) => setHoldDialog((h) => ({ ...h, open: v }))}
        transaction={holdDialog.tx}
        fee={holdDialog.fee}
        balances={balances}
        onPaid={() => reload()}
      />
    </div>
  );
}
